import React, { useEffect } from "react";
import { useGeoFence } from "../../hooks/useGeoFence";
import GeoFenceStatus from "./GeoFenceStatus";
import { Navigation, Loader2 } from "lucide-react";
import { GEOFENCE_STATUS } from "../../services/geofenceService";

export default function GeoFenceGuard({
  siteId,
  actionName = "this action",
  children,
  autoVerify = true,
  fallback = null,
  showStatusBanner = true,
}) {
  const {
    siteConfig,
    userLocation,
    accuracy,
    distance,
    status,
    isLoading,
    isCapturing,
    error,
    captureCurrentLocation,
    isValidated,
  } = useGeoFence(siteId, { autoFetch: true, autoCapture: autoVerify });

  // Auto-verify on mount if enabled
  useEffect(() => {
    if (autoVerify && siteId && status === GEOFENCE_STATUS.IDLE) {
      captureCurrentLocation();
    }
  }, [autoVerify, siteId, status, captureCurrentLocation]);

  // Loading site configuration
  if (isLoading && !siteConfig) {
    return (
      <div className="p-6 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center gap-3 text-gray-600 text-sm">
        <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
        <span>Loading site geofence configuration...</span>
      </div>
    );
  }

  // Location successfully verified (or site geofence is disabled)
  if (isValidated) {
    const renderPayload = {
      location: userLocation,
      accuracy,
      distance,
      siteConfig,
      isValidated: true,
      status,
    };

    return (
      <div className="space-y-4">
        {showStatusBanner && (
          <GeoFenceStatus
            status={status}
            distance={distance}
            accuracy={accuracy}
            radius={siteConfig?.geofence_radius || 100}
            siteName={siteConfig?.name}
            compact
          />
        )}
        {typeof children === "function" ? children(renderPayload) : children}
      </div>
    );
  }

  // If custom fallback provided
  if (fallback) {
    return fallback({
      status,
      distance,
      accuracy,
      siteConfig,
      error,
      retry: captureCurrentLocation,
      isCapturing,
    });
  }

  // Default Guard UI blocking restricted action
  return (
    <div className="p-6 rounded-2xl border border-gray-200 bg-white shadow-sm space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="font-bold text-gray-900 text-base sm:text-lg">
            Site Location Verification Required
          </h3>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            Physical presence at <span className="font-semibold">{siteConfig?.name || "the site"}</span> is required to {actionName}.
          </p>
        </div>

        <button
          type="button"
          onClick={captureCurrentLocation}
          disabled={isCapturing}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 active:scale-95 text-white font-semibold text-sm shadow-md transition disabled:opacity-60"
        >
          {isCapturing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Verifying GPS...</span>
            </>
          ) : (
            <>
              <Navigation className="w-4 h-4" />
              <span>Verify Location</span>
            </>
          )}
        </button>
      </div>

      {status !== GEOFENCE_STATUS.IDLE && (
        <GeoFenceStatus
          status={status}
          distance={distance}
          accuracy={accuracy}
          radius={siteConfig?.geofence_radius || 100}
          siteName={siteConfig?.name}
          onRetry={captureCurrentLocation}
        />
      )}
    </div>
  );
}
